import React, { useState, useEffect } from 'react';
import { UserCircle } from 'lucide-react';
import { supabase } from '../../../../supabaseClient';

interface CommentItem {
  id: string | number;
  user: string | null;
  avatar: string | null;
  text: string;
  date: string;
}

export default function ReviewChat({ videoId, onCommentAdded }: { videoId: string, onCommentAdded?: (newTotal: number) => void }) {
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [newComment, setNewComment] = useState('');
  const [optionalName, setOptionalName] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 10;

  // Puxa os comentários aplicando a regra de validade de 1 ano
  const fetchComments = async (pageToFetch: number) => {
    const from = pageToFetch * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    // Calcula a data exata de 1 ano atrás
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    const { data, error } = await supabase
      .from('comments')
      .select('*')
      .eq('video_id', videoId)
      .gte('created_at', oneYearAgo.toISOString()) // 🚀 REGRA DE 1 ANO: Ignora comentários mais antigos que 365 dias
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Erro ao buscar comentários:', error);
    } else if (data) {
      if (data.length < PAGE_SIZE) setHasMore(false);

      const formatted = data.map((c: any) => {
        let formattedDate = 'Hoje';
        if (c.created_at) {
          const dateObj = new Date(c.created_at);
          const day = dateObj.toLocaleDateString('pt-BR');
          const time = dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
          formattedDate = `${day} às ${time}`;
        }

        return {
          id: c.id,
          user: c.user_name || null,
          avatar: c.user_name && c.user_name !== 'Usuário Anônimo' ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${c.user_name}` : null,
          text: c.text || '',
          date: formattedDate
        };
      });

      setComments(prev => pageToFetch === 0 ? formatted : [...prev, ...formatted]);
    }
  };

  useEffect(() => {
    if (videoId) {
      setPage(0);
      setHasMore(true);
      fetchComments(0);
    }
  }, [videoId]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchComments(nextPage);
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    setLoading(true);

    const userName = optionalName.trim() ? optionalName : 'Usuário Anônimo';
    const avatarUrl = optionalName.trim() && optionalName !== 'Usuário Anônimo' ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${optionalName}` : null;

    const { data, error } = await supabase
      .from('comments')
      .insert([
        {
          video_id: videoId,
          text: newComment,
          user_name: userName
        }
      ])
      .select();

    if (error) {
      console.error('Erro detalhado do Supabase:', error);
      alert(`Erro ao enviar: ${error.message}`);
    } else {
      const saved = data ? data[0] : null;
      const newTotalCount = comments.length + 1;

      await supabase
        .from('videos')
        .update({ comments_count: newTotalCount })
        .eq('id', videoId);

      const now = new Date();
      const currentFormattedDate = `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;

      const newEntry: CommentItem = {
        id: saved?.id || Date.now(),
        user: userName !== 'Usuário Anônimo' ? userName : null,
        avatar: avatarUrl,
        text: newComment,
        date: currentFormattedDate
      };
      setComments([newEntry, ...comments]);
      setNewComment('');
      setOptionalName('');

      if (onCommentAdded) {
        onCommentAdded(newTotalCount);
      }
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: '1100px', margin: '40px auto', padding: '0 40px 60px 40px', boxSizing: 'border-box' }}>
      <h3 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', color: '#fff' }}>
        Avaliações dos Usuários <span style={{ fontSize: '14px', color: '#aaa', fontWeight: 'normal' }}>({comments.length})</span>
      </h3>

      <div style={{ display: 'flex', gap: '15px', marginBottom: '35px', alignItems: 'flex-start' }}>
        <div style={{ width: '45px', height: '45px', borderRadius: '50%', backgroundColor: '#222', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', flexShrink: 0 }}>
          <UserCircle size={30} />
        </div>
        
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <input 
            type="text" 
            placeholder="Seu nome (Opcional - deixe em branco para Anônimo)" 
            value={optionalName}
            onChange={(e) => setOptionalName(e.target.value)}
            style={{ width: '100%', backgroundColor: '#111', border: '1px solid #333', color: '#fff', padding: '10px 12px', borderRadius: '4px', fontSize: '13px', outline: 'none' }} 
          />
          <textarea 
            placeholder="Adicione um comentário ou avaliação sobre este título..." 
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            style={{ width: '100%', minHeight: '70px', backgroundColor: '#111', border: '1px solid #333', color: '#fff', padding: '12px', borderRadius: '4px', fontSize: '14px', resize: 'vertical', outline: 'none' }} 
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button 
              onClick={handleComment}
              disabled={loading}
              style={{ backgroundColor: '#E50914', color: '#fff', border: 'none', padding: '8px 24px', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Enviando...' : 'Comentar'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {comments.map(comment => (
          <div key={comment.id} style={{ display: 'flex', gap: '15px' }}>
            {comment.avatar ? (
              <img src={comment.avatar} alt={comment.user || 'User'} style={{ width: '45px', height: '45px', borderRadius: '50%', backgroundColor: '#222', flexShrink: 0 }} />
            ) : (
              <div style={{ width: '45px', height: '45px', borderRadius: '50%', backgroundColor: '#222', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', flexShrink: 0 }}>
                <UserCircle size={28} />
              </div>
            )}

            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '6px' }}>
                <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#fff' }}>
                  {comment.user ? comment.user : 'Usuário Anônimo'}
                </span>
                <span style={{ fontSize: '12px', color: '#777' }}>{comment.date}</span>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#ddd', lineHeight: '1.5' }}>{comment.text}</p>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: '35px' }}>
          <button 
            onClick={loadMore}
            style={{
              backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
              padding: '10px 24px', borderRadius: '4px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer'
            }}
          >
            Carregar mais comentários (10 em 10)
          </button>
        </div>
      )}
    </div>
  );
}